import { DialogDescription } from '@radix-ui/react-dialog'
import { cn } from '@scalis/utils'
import { Button, ButtonKind, ButtonWidth } from '../../button'
import { Input } from '../../input'
import { Select } from '../../select'
import { Body, BodySize, Title, TitleSize } from '../../typography'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '../dialog'
import { iconStyles } from './confirmation-dialog.styles'
import { ConfirmationDialogKind, ConfirmationDialogProps } from './confirmation-dialog.types'

export const ConfirmationDialog = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  onOpenChange,
  icon,
  hasCancelButton = true,
  disabled,
  description,
  loading,
  kind = ConfirmationDialogKind.information,
  className,
  onInteractOutside,
  onEscapeKeyDown,
  onPointerDown,
  overlayClassName,
  confirmationText,
  confirmationInputProps,
  destructiveActionText,
  destructiveActionInputProps,
  confirmationValue,
  onConfirmationChange,
  destructiveValue,
  onDestructiveChange,
  selectLabel,
  selectOptions = [],
  selectValue,
  onSelectChange,
  ...props
}: ConfirmationDialogProps) => {
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onConfirmationChange?.('')
      onDestructiveChange?.('')
    }
    onOpenChange?.(open)
  }

  const handleConfirm = () => {
    onConfirm?.()
    onConfirmationChange?.('')
    onDestructiveChange?.('')
  }

  const isConfirmDisabled = destructiveActionText ? destructiveValue !== destructiveActionText || disabled : disabled

  if (!isOpen) {
    return null
  }

  const labelConfirm = props.labelConfirm ?? 'Confirm'
  const labelCancel = props.labelCancel ?? 'Cancel'

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogContent
        className={cn('flex w-[375px] flex-col gap-0 rounded-xl p-6', className)}
        onEscapeKeyDown={onEscapeKeyDown}
        onPointerDown={onPointerDown}
        onInteractOutside={onInteractOutside}
        overlayClassName={overlayClassName}
      >
        <DialogDescription className='hidden'>{description ?? title}</DialogDescription>

        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-2'>
            <i className={iconStyles({ kind })} />
            <DialogTitle>
              <Title
                size={TitleSize.extraSmall}
                asChild
              >
                <span>{title}</span>
              </Title>
            </DialogTitle>
          </div>

          <div className='flex flex-col gap-4'>
            <Body size={BodySize.small}>{message}</Body>

            {confirmationText && (
              <div>
                <div className='mb-2 text-sm text-neutral-secondary'>
                  Type <strong>{confirmationText}</strong> to confirm
                </div>
                <Input
                  value={confirmationValue}
                  onChange={(e) => onConfirmationChange?.(e.target.value)}
                  placeholder={confirmationInputProps?.placeholder ?? `Type ${confirmationText} to confirm`}
                  className={confirmationInputProps?.className}
                />
              </div>
            )}

            {destructiveActionText && (
              <div>
                <div className='mb-2 text-sm text-neutral-secondary'>
                  Type <strong>{destructiveActionText}</strong> to confirm
                </div>
                <Input
                  value={destructiveValue}
                  onChange={(e) => onDestructiveChange?.(e.target.value)}
                  placeholder={destructiveActionInputProps?.placeholder ?? `Type ${destructiveActionText} to confirm`}
                  className={destructiveActionInputProps?.className}
                />
              </div>
            )}

            {selectLabel && (
              <Select
                label={selectLabel}
                options={selectOptions ?? []}
                value={selectValue}
                setValue={(_, value) => onSelectChange?.(Array.isArray(value) ? value[0] : value)}
              />
            )}
          </div>
        </div>

        <div className='mt-6 flex w-full gap-2'>
          {hasCancelButton && (
            <DialogTrigger asChild>
              <Button
                width={ButtonWidth.full}
                kind={ButtonKind.secondary}
                onClick={onCancel}
                aria-label={labelCancel}
                disabled={loading}
              >
                {labelCancel}
              </Button>
            </DialogTrigger>
          )}

          <Button
            kind={kind === ConfirmationDialogKind.information ? ButtonKind.primary : ButtonKind.critical}
            width={ButtonWidth.full}
            disabled={isConfirmDisabled || loading}
            loading={loading}
            onClick={handleConfirm}
            aria-label={labelConfirm}
          >
            {labelConfirm}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
